﻿import { useState, useEffect, useRef } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import FilePreview from '../Common/FilePreview';
import FileLoader from '../Common/FileLoader';
import TextBoxEditToggle from '../Common/TextBoxEditToggle';
import ConfirmationDialog from '../Common/ConfirmationDialog';
import DocumentHistory from './DocumentHistory';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { BlobMetadata } from "../../models/Blobs/blob-metadata";
import { BlobStatus, BLOB_STATUS_OPTIONS } from "../../models/Blobs/blob-status";
import { FileMetadata } from '../../models/Common/file-metadata';
import { DatabaseHistoryRow } from '../../models/Auditing/database-history-row';
import { LookupItem } from '../../models/lookup-item';

import { Button } from 'devextreme-react/button';
import { Form, SimpleItem, GroupItem } from 'devextreme-react/form';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { TextBox } from 'devextreme-react';

interface Props {
    blobMetadata: BlobMetadata;
    categoriesLookup: LookupItem[];
    showFilePreview: boolean;
    onDirtyStatusChange: (isDirty: boolean) => void;
    onSaveSuccess: (newData: BlobMetadata) => void;
    onCancelRequest: () => void;
    showToast: (message: string, toastType: string) => void;
}


export default function DocumentDetail(props: Props) {
    const readOnly = props.blobMetadata.blobStatusId === BlobStatus.Approved;

    const [localMetadata, setLocalMetadata] = useState<BlobMetadata | null>(null);

    const [metadataHistory, setMetadataHistory] = useState<DatabaseHistoryRow[]>([]);

    const [hasChanges, setHasChanges] = useState(false);

    // Holds the local File (for pending uploads) OR the Storage URL (for viewing)
    const [fileSource, setFileSource] = useState<FileMetadata | null>(null);

    // Used for deciding <FilePreview> or <FileLoader>; do not use to determine save logic
    const [isReplacingFile, setIsReplacingFile] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    const [saveButtonText, setSaveButtonText] = useState('Save');

    const [isHistoryLoading, setIsHistoryLoading] = useState(true);

    const [isBlobLoading, setIsBlobLoading] = useState(false);

    const [blobError, setBlobError] = useState(false);

    const [showConfirmCopyToNextVersion, setShowConfirmCopyToNextVersion] = useState(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] = useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] = useState<boolean>()

    const isDirty = hasChanges || !!fileSource?.file;

    const isNewRecord = !localMetadata?.blobId;

    useEffect(() => {
        props.onDirtyStatusChange(isDirty);
    }, [isDirty]);

    const formRef = useRef(null);

    // Keep track of the previous blob URL so it can be revoked when a new one is created or on unmount
    const prevBlobUrlRef = useRef<string | null>(null);

    const revokeIfBlob = (url?: string | null) => {
        if (url && url.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        }
    };

    useEffect(() => {
        setHasChanges(false);
        setIsSaving(false);
        setSaveButtonText("Save");
        setIsReplacingFile(false);
        setBlobError(null);
        setLocalMetadata({ ...props.blobMetadata });

        if (props.blobMetadata?.blobId) {
            setIsHistoryLoading(true);
            fetchBlobMetadataHistory(props.blobMetadata.blobId);
            if (props.showFilePreview)
                fetchBlob(props.blobMetadata.blobId, props.blobMetadata.fileName);
        } else {
            // closing or starting a new upload or
            // switching to a record with no file
            // revoke any previous preview URL we created (memory management)
            revokeIfBlob(prevBlobUrlRef.current);
            prevBlobUrlRef.current = null;
            setFileSource(null);
        }

        // On unmount, revoke any remaining blob URL we created
        return () => {
            revokeIfBlob(prevBlobUrlRef.current);
            prevBlobUrlRef.current = null;
        };

    }, [props.blobMetadata]); // Re-run when user selects a different document


    /* When the user's local selection (File from FileLoader) changes,
     * convert to FileMetadata and create a blob URL.
     * Then revoke the previous blob URL before setting a new one, for memory management
     */
    const handleFileSelected = (file: File | null) => {
        revokeIfBlob(prevBlobUrlRef.current);
        prevBlobUrlRef.current = null;

        if (!file) {
            setFileSource(null);
            return;
        }

        const url = URL.createObjectURL(file);
        prevBlobUrlRef.current = url;

        setFileSource({
            url,
            name: file.name,
            contentType: file.type || null,
            file: file
        });

        setLocalMetadata(prev => ({
            ...prev,
            fileName: file.name
        }));

        setIsReplacingFile(false);
    };

    const fetchBlob = async (blobId: string, friendlyFileName: string) => {
        setIsBlobLoading(true);
        setBlobError(false);
        try {
            const blob: Blob = await CrudFunctions.getDataBlob(`blobs/${blobId}/file`);

            // Create a temporary URL for the browser to display blob without having to re-download it via public URL
            const objectUrl = URL.createObjectURL(blob);

            // Revoke the old one before replacing it
            revokeIfBlob(prevBlobUrlRef.current);
            prevBlobUrlRef.current = objectUrl;

            setFileSource({
                url: objectUrl,
                name: friendlyFileName,
                contentType: blob.type || null,
                file: null
            });
        } catch (error) {
            setBlobError(true);
            showMessage(error, ModalMessageType.Error, "Failed to fetch blob");
            setFileSource(null);
        } finally {
            setIsBlobLoading(false);
        }
    };

    const fetchBlobMetadataHistory = async (blobId: string) => {
        try {
            const history: DatabaseHistoryRow[] = await CrudFunctions.getData(`auditing/blobs-status-history/${blobId}`);
            setMetadataHistory(history);
            return true;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Retrieving Blobs History data");
            return false;
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!fileSource) return;

        let downloadUrl = fileSource.url;
        const fileName = fileSource.name || 'download';

        try {
            // If it's not a blob URL, fetch it and create a temporary blob URL
            let tempCreated = false;
            if (!downloadUrl.startsWith('blob:')) {
                const response = await fetch(downloadUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                downloadUrl = URL.createObjectURL(blob);
                tempCreated = true; // only revoke if we created it here
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the temporary URL created for download; don't revoke the persistent preview URL here
            if (tempCreated) {
                URL.revokeObjectURL(downloadUrl);
            }
            return true;

        } catch (error) {
            console.error('Download failed', error);
            showMessage(error, ModalMessageType.Error, "An error occurred while downloading the document.");
            // Fallback: open in new tab if it's a remote URL
            if (!fileSource.url.startsWith('blob:')) {
                window.open(fileSource.url, '_blank');
            }
            return false;
        }
    };

    const handleSave = async (metadataToSave: BlobMetadata, fileSource: FileMetadata | null) => {
        // If blobId does not exist, create a new record
        // If blobId exists and no file changes, update the metadata for existing blob
        // If blobId exists but file replaced, overwrite blob & update metadata

        const validationResult = formRef.current.instance().validate();
        if (!validationResult.isValid) {
            validationResult.brokenRules[0].validator.focus();
            return;
        }

        setIsSaving(true);
        setSaveButtonText("Saving...");
        try {
            const isNewRecord = !metadataToSave.blobId;
            const hasSelectedFile = !!fileSource?.file;

            if (isNewRecord) {
                if (!hasSelectedFile) throw new Error("A file is required when creating a new record.");

                // CASE: New Document (requires file + metadata)
                const formData = new FormData();
                formData.append('file', fileSource!.file);
                formData.append('StorageContainerId', metadataToSave.storageContainerId.toString());
                formData.append('FileName', metadataToSave.fileName);
                formData.append('Description', metadataToSave.description || '');
                formData.append('Title', metadataToSave.title || '');
                formData.append('ControlledDocumentId', metadataToSave.controlledDocumentId || '');
                formData.append('BlobStatusId', metadataToSave.blobStatusId.toString());

                const savedDoc = await CrudFunctions.postData(`blobs`, formData);
                props.onDirtyStatusChange(false);
                props.onSaveSuccess(savedDoc);
                props.showToast("New record created successfully", "success");
            }
            else if (hasSelectedFile) {
                // CASE: If blobId exists but file replaced, update blob & update metadata
                const formData = new FormData();
                formData.append('file', fileSource!.file);
                formData.append('FileName', metadataToSave.fileName);
                formData.append('Description', metadataToSave.description || '');
                formData.append('Title', metadataToSave.title || '');
                formData.append('ControlledDocumentId', metadataToSave.controlledDocumentId || '');
                formData.append('BlobStatusId', metadataToSave.blobStatusId.toString());

                const url = `blobs/${metadataToSave.blobId}/file`;
                const updatedDoc = await CrudFunctions.putData(url, formData);

                setIsReplacingFile(false);
                props.onDirtyStatusChange(false);
                props.onSaveSuccess(updatedDoc);
                props.showToast("File and metadata overwritten", "success");

            } else {
                // CASE: Metadata Update only (existing record, no new file)
                const updatePayload = {
                    fileName: metadataToSave.fileName,
                    description: metadataToSave.description,
                    title: metadataToSave.title,
                    controlledDocumentId: metadataToSave.controlledDocumentId,
                    blobStatusId: Number(metadataToSave.blobStatusId)
                };

                const updatedDoc = await CrudFunctions.putData(`blobs/${metadataToSave.blobId}/metadata`, updatePayload);

                props.onDirtyStatusChange(false);
                props.onSaveSuccess(updatedDoc);
                props.showToast("Metadata updated", "success")
            }

            setHasChanges(false);
            setIsReplacingFile(false);
            setFileSource(prev => ({
                ...prev,
                file: null
            }));
            return true;

        } catch (error) {
            if (error instanceof Error) {
                // Client-side validation
                const mockResponse = {
                    json: async () => ({
                        message: error.message
                    })
                };
                showMessage(mockResponse, ModalMessageType.Error, "Validation Error");
            } else {
                // API error
                showMessage(error, ModalMessageType.Error, "An error occurred while saving.");
            }
            return false;
        }
        finally {
            setSaveButtonText('Save');
            setIsSaving(false);
        }
    }

    // Copy blob & metadata: new blob upload is assigned new id and version 1
    const handleCopyToNewDocument = async (metadataToCopy: BlobMetadata) => {
        setIsSaving(true);
        setIsBlobLoading(true);
        try {
            const newMetadata = await CrudFunctions.postData(`blobs/${metadataToCopy.blobId}/copy`, {});
            props.onSaveSuccess(newMetadata);
            props.showToast("Copy created", "success")
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Copy Failed!");
        }
        finally {
            setIsSaving(false);
            setIsBlobLoading(false);
        }
    }

    // Copies blob & metadata: new blob upload with version increment
    const confirmCopyToNextVersion = () => {
        setShowConfirmCopyToNextVersion(true);
    }

    const executeCopyToNextVersion = async () => {
        setIsSaving(true);
        setIsBlobLoading(true);
        const metadataToCopy = localMetadata;
        try {
            const newMetadata = await CrudFunctions.postData(`blobs/${metadataToCopy.blobId}/versions`, {});
            props.onSaveSuccess(newMetadata);
            props.showToast("Next Version created", "success")
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Next Version Failed!");
        }
        finally {
            setIsSaving(false);
            setIsBlobLoading(false);
            setShowConfirmCopyToNextVersion(false);
        }
    }

    const showMessage = (res: any, mode: ModalMessageType, title: string) => {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    if (!localMetadata) return <div>Loading...</div>;

    return (
        <>
            <div className="document-detail-container" id="document-detail-container">
                <div className="document-detail-container-row">

                    {/* LEFT SIDE: FILE UPLOAD/PREVIEW */}
                    <div className="document-detail-left-side-container">
                        <div className="document-detail-file-container">
                            {!props.showFilePreview && !isNewRecord ? (
                                <p>Preview unavailable ('Show File Previews' is disabled on previous page)</p>
                            ) : isBlobLoading ? (
                                <p>Loading document preview...</p>
                            ) : blobError ? (
                                <div className="error-placeholder">
                                    <p>Preview unavailable. You can still edit metadata.</p>
                                    <Button text="Retry document fetch"
                                        icon="restore"
                                        type="default"
                                        stylingMode="contained"
                                        onClick={() => fetchBlob(localMetadata.blobId, localMetadata.fileName)} />
                                </div>
                            ) : (fileSource && !isReplacingFile) ? (
                                <FilePreview file={fileSource} canRemove={false}
                                //onRemove={() => {
                                //    if (readOnly) return;
                                //    revokeIfBlob(prevBlobUrlRef.current);
                                //    prevBlobUrlRef.current = null;
                                //    setFileSource(null);
                                //}}
                                />
                            ) : (
                                <FileLoader
                                    blacklistExtensions={['.exe', '.dll', '.jar']}
                                    onFileSelected={(file) => {
                                        handleFileSelected(file);
                                    }}
                                    onError={(message) => {
                                        props.showToast(message, 'error');
                                    }}
                                /*title={isOverwritingFile ? "Select replacement file" : "Upload file"}*/
                                />
                            )}
                        </div>

                        {/* FILE ACTIONS ROW */}
                        <div className="document-detail-file-actions">
                            {/* DOWNLOAD BUTTON: Only show if fileSource or download error exists */}
                            {(fileSource || blobError) && !isReplacingFile && (
                                <Button
                                    className="document-detail-button-wide"
                                    text="Download"
                                    icon="download"
                                    type="default"
                                    stylingMode="contained"
                                    onClick={handleDownload}
                                />
                            )}

                            {/* REPLACE BUTTON */}
                            {fileSource && !isReplacingFile && !readOnly && (
                                <Button
                                    className="document-detail-button-wide"
                                    text="Replace"
                                    icon="upload"
                                    type="default"
                                    stylingMode="outlined"
                                    disabled={isSaving}
                                    onClick={() => {
                                        setFileSource(null);
                                        setHasChanges(true);
                                        setIsReplacingFile(true);
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    {/* RIGHT SIDE: METADATA */}
                    <div className="document-detail-right-side-container">
                        {localMetadata.blobId && (
                            <div className="document-detail-top-button-row">
                                {/* NOTE: hiding for now per Jenna */}
                                {/* <Button
                                    text="Copy to new document"
                                    icon="copy"
                                    type="default"
                                    onClick={() => handleCopyToNewDocument(localMetadata)}
                                /> */}
                                <Button
                                    text="Copy to new version"
                                    icon="chevrondoubleright"
                                    type="default"
                                    onClick={confirmCopyToNextVersion}
                                />
                            </div>
                        )}

                        <Form
                            ref={formRef}
                            formData={localMetadata}
                            readOnly={readOnly}
                            disabled={isBlobLoading}
                            labelLocation="left"
                            showRequiredMark={false}
                            showColonAfterLabel={false}
                            onFieldDataChanged={() => setHasChanges(true)}
                        >
                            <GroupItem caption="Document Metadata">
                                <SimpleItem
                                    dataField="storageContainerId"
                                    label={{ text: "Category" }}
                                    editorType="dxSelectBox"
                                    editorOptions={{
                                        items: props.categoriesLookup,
                                        displayExpr: 'name',
                                        valueExpr: 'id',
                                        readOnly: !isNewRecord
                                    }}
                                    isRequired={true}
                                />
                                <GroupItem colCount={3}>
                                    <SimpleItem dataField="versionNumber" label={{ text: "Version" }} editorOptions={{ readOnly: true }} />
                                    <SimpleItem dataField="sizeBytes" editorOptions={{ readOnly: true }} visible={!isNewRecord} />
                                    <SimpleItem dataField="mimeType" editorOptions={{ readOnly: true }} visible={!isNewRecord} />
                                </GroupItem>
                                <SimpleItem dataField="fileName" editorType="dxTextBox" editorOptions={{ trim: true, readOnly: true }} visible={!isNewRecord} isRequired={true} />
                                <SimpleItem dataField="title" render={() => (
                                    <>
                                        {isNewRecord
                                            ? <TextBox
                                                value={localMetadata?.title}
                                                onValueChanged={(e) => {
                                                    setLocalMetadata(prev => ({ ...prev, title: e.value }));
                                                    setHasChanges(true);
                                                }}
                                            >
                                            </TextBox>
                                            : <TextBoxEditToggle
                                                value={localMetadata?.title}
                                                readOnly={readOnly}
                                                onValueChanged={(val) => {
                                                    setLocalMetadata(prev => ({ ...prev, title: val }));
                                                    setHasChanges(true);
                                                }}
                                                editHint="Click to edit title"
                                                warningMessage="Caution: Saving a new title will update all versions of the document!"
                                            />
                                        }
                                    </>
                                )} />
                                <SimpleItem dataField="controlledDocumentId" label={{ text: "Document Id" }} editorType="dxTextBox" editorOptions={{ trim: true, maxLength: 30 }} />

                                <SimpleItem
                                    dataField="blobStatusId"
                                    label={{ text: "Status" }}
                                    editorType="dxSelectBox"
                                    editorOptions={{
                                        items: BLOB_STATUS_OPTIONS,
                                        displayExpr: 'label',
                                        valueExpr: 'value'
                                    }}
                                />
                                <SimpleItem dataField="description" editorType="dxTextArea" editorOptions={{ trim: true }} />

                                {/* <SimpleItem label={{ text: "Tags" }}>
                                    <div>Coming Soon!</div>
                                </SimpleItem> */}
                            </GroupItem>
                        </Form>

                        {localMetadata.blobId &&
                            <DocumentHistory data={metadataHistory} isLoading={isHistoryLoading} />
                        }
                    </div>
                </div>

                {!readOnly && (
                    <div className="document-detail-metadata-actions">

                        <Button
                            className="document-detail-button-wide"
                            type="success"
                            disabled={!isDirty || isSaving}
                            onClick={() => handleSave(localMetadata, fileSource)}
                        >
                            <LoadIndicator className="button-indicator" visible={isSaving} />
                            <span className="dx-button-text">{saveButtonText}</span>
                        </Button>

                        {props.onCancelRequest && (
                            <Button
                                className="document-detail-button-wide"
                                text="Cancel"
                                type="normal"
                                stylingMode="outlined"
                                disabled={isSaving}
                                onClick={props.onCancelRequest}
                            />
                        )}
                    </div>
                )}

                <ConfirmationDialog
                    visible={showConfirmCopyToNextVersion}
                    title="Confirm Copy to New Version"
                    message="Are you sure you want to create a new version based on this one?"
                    primaryActionText='Yes, Copy'
                    primaryActionIcon='check'
                    onPrimaryAction={executeCopyToNextVersion}
                    secondaryActionText='No, Cancel'
                    onSecondaryAction={() => setShowConfirmCopyToNextVersion(false)}
                    onHiding={() => setShowConfirmCopyToNextVersion(false)}
                />

                {
                    globalDialogVisible &&
                    <GlobalDialog metadata={globalDialogMetadata} />
                }
            </div>

        </>
    );
};